import type { components } from "@/api-schema";
import { getCoords } from "@/common/helpers/get-coords";
import { RelationLink } from "@/components/relation-link/relation-link";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useMapTheme } from "@/components/deck-map/_helpers/map-theme";
import { MapPinIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { RouteMap } from "./_components/route-map";
import { RouteWaypoint } from "./_components/route-waypoint";

type RelocationOrder = components["schemas"]["RelocationOrder"];
type Branch = components["schemas"]["Branch"];
type Address = components["schemas"]["Address"];

interface RelocationOrderDetailRouteCardProps {
  relocationOrder: RelocationOrder;
}

function renderLocationName(branch: Branch | null, address: Address | null | undefined): ReactNode {
  if (!branch) {
    return address?.city ?? null;
  }

  return (
    <RelationLink subject="Branch" uuid={branch.uuid}>
      {branch.name}
    </RelationLink>
  );
}

export function RelocationOrderDetailRouteCard({ relocationOrder }: RelocationOrderDetailRouteCardProps) {
  const { t } = useTranslation(["fit", "common"]);
  const palette = useMapTheme();

  const originLabel = renderLocationName(relocationOrder.originBranch, relocationOrder.originAddress);
  const destinationLabel = renderLocationName(relocationOrder.destinationBranch, relocationOrder.destinationAddress);

  const originCoords = getCoords(relocationOrder.originAddress, relocationOrder.originBranch);
  const destinationCoords = getCoords(relocationOrder.destinationAddress, relocationOrder.destinationBranch);
  const currentCoords = getCoords(relocationOrder.currentAddress, relocationOrder.currentBranch);
  const hasMap = originCoords != null && destinationCoords != null;

  const currentBranch = relocationOrder.currentBranch;
  const isCurrentAtOrigin = currentBranch != null && currentBranch.uuid === relocationOrder.originBranch?.uuid;
  const isCurrentAtDestination =
    currentBranch != null && currentBranch.uuid === relocationOrder.destinationBranch?.uuid;
  const currentBranchLabel =
    !isCurrentAtOrigin && !isCurrentAtDestination
      ? renderLocationName(currentBranch, relocationOrder.currentAddress)
      : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <MapPinIcon className="h-5 w-5" />
          {t("fit:Route")}
        </CardTitle>
      </CardHeader>

      <CardContent>
        <div className="flex flex-col gap-6 sm:flex-row">
          <div className="flex min-w-0 flex-1 flex-col justify-start gap-4">
            <RouteWaypoint
              label={t("fit:From")}
              name={originLabel}
              address={relocationOrder.originAddress}
              isCurrent={isCurrentAtOrigin}
            />

            {currentBranchLabel !== null && (
              <RouteWaypoint
                label={t("fit:CurrentBranch")}
                name={currentBranchLabel}
                address={relocationOrder.currentAddress}
                isCurrent
                isIntermediate
              />
            )}

            <RouteWaypoint
              label={t("fit:To")}
              name={destinationLabel}
              address={relocationOrder.destinationAddress}
              isCurrent={isCurrentAtDestination}
            />
          </div>

          {hasMap && (
            <div className="min-w-0 flex-1">
              <RouteMap
                originCoords={originCoords}
                destinationCoords={destinationCoords}
                currentCoords={currentCoords}
                palette={palette}
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
